import * as React from 'react'
import { ThemeProvider as MDSThemeProvider } from '@mds/mds-reactjs-library'
import styled, { ThemeProvider } from 'styled-components'
import GlobalStyle from './global-styles'
import Footer from '../components/Footer'
import Header from '../components/Header'
import type { Customization } from '../types/Customization'
import { APP_MAX_WIDTH_NARROW } from '../util/layoutWidth'

/**
 * MDS Breakpoints are defined at
 * https://mds.mckinsey.com/develop/react/components/theming/breakpoints
 */
const breakpoints = {
  keys: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'],
  lowBoundaryValues: [0, 320, 576, 768, 1024, 1200, 1440, 1920],
  highBoundaryValues: [319, 575, 767, 1023, 1199, 1439, 1919],
  getBreakpointLowBoundary: 'function',
  getBreakpointLowBoundaryRedundantly: 'function',
  getBreakpointHighBoundary: 'function',
  getBreakpointHighBoundaryRedundantly: 'function',
  xxs: 0,
  xs: 320,
  sm: 576,
  md: 768,
  lg: 1024,
  xl: 1200,
  xxl: 1440,
  xxxl: 1920,
}

const StyleWrapper = styled.div`
  max-width: ${(props): string => props.theme.appMaxWidth};
  margin: 0 auto;
  padding: 0 1rem;

  display: flex;
  flex-direction: column;
`

const ViewWrapper = styled.div`
  flex-grow: ${(props): number => props.theme.cardFlexGrow ?? 0};
  margin: 1rem 1rem 0 1rem;
  box-shadow: ${(props) => (props.theme.doesItHaveCustomBackground ? 'none' : '#ddd 0px 0px 10px 0px')};
  background-color: ${
    /* istanbul ignore next */
    (props) => props.theme.mainFrameBackgroundColor
  };
`

type AppViewWrapperProps = {
  children: JSX.Element | JSX.Element[]
}
export const AppViewWrapper = ({ children }: AppViewWrapperProps) => <ViewWrapper>{children}</ViewWrapper>

export const mckIdDefaultTheme = {
  appMaxWidth: APP_MAX_WIDTH_NARROW,
  cardFlexGrow: 0,
  desktopBreakpoint: `@media screen and (min-width: ${breakpoints.md}px)`,
  backgroundColor: '#ffffff',
  mainFrameBackgroundColor: '#ffffff',
  headerBackgroundColor: '#ffffff',
  footerTextShadow: '0px 0px 1px #000',
}

const packageJson = require('../../package.json') // eslint-disable-line @typescript-eslint/no-var-requires
const version = `v${packageJson.version || '0'}`

type McKinseyIDThemeProps = {
  children: React.ReactNode
  customization?: Customization
  layoutTheme?: Record<string, unknown>
}

const getTheme = ({ customization, layoutTheme = {} }) => {
  const doesItHaveCustomBackground = !!(
    customization?.backgroundColor?.length || customization?.backgroundImageUrl?.length
  )

  return {
    ...mckIdDefaultTheme,
    ...layoutTheme,
    doesItHaveCustomBackground,
    backgroundColor: customization?.backgroundColor || mckIdDefaultTheme.backgroundColor,
    backgroundImageUrl: customization?.backgroundImageUrl?.length
      ? `url(${customization.backgroundImageUrl})`
      : undefined,
    footerTextShadow: doesItHaveCustomBackground ? mckIdDefaultTheme.footerTextShadow : undefined,
  }
}

const McKinseyIDTheme = ({ customization, layoutTheme, children }: McKinseyIDThemeProps) => (
  <ThemeProvider theme={getTheme({ customization, layoutTheme })}>
    <MDSThemeProvider>
      <GlobalStyle />
      <StyleWrapper>
        <AppViewWrapper>
          <Header
            bannerLogoUrl={customization?.bannerLogoUrl}
            secondBannerLogoUrl={customization?.secondBannerLogoUrl}
            bannerBackgroundColor={customization?.bannerBackgroundColor}
          />
          {children}
        </AppViewWrapper>
        <Footer version={version} />
      </StyleWrapper>
    </MDSThemeProvider>
  </ThemeProvider>
)

McKinseyIDTheme.defaultProps = {
  children: null,
}

export default McKinseyIDTheme
